// @givmo/fundraisers-module — a campaign's graphics to share (EMC-BE23's web half; D-EMC-23 (2) as amended by
// Jonathan, 2026-11-03 16:52): the pictures from the organization's Collateral that everyone who shares the
// campaign, or a fundraiser page under it, gets in their share kit to download and post with their link, picked
// once here, in order, up to the server's limit (8). Its own card right after Joining on the Campaign tab
// (Fundraisers & Campaigns' placement, revised after the mockup); a list, so it keeps its heading at one
// picture. The campaign's own Share card shows the same picks, and a new Board Ask's attachments start
// from them.
//
// `GET|PUT /org/{org_ref}/fundraisers/{id}/share-graphics` is ahead of the committed snapshot, so the calls use
// the configured `givmoClient` (the Social tab's precedent). Dark (the share kit off) is the family's route
// miss: the section isn't drawn. Each pick's picture is read from Collateral by id; one no longer current
// there (out of date, or dated for a day gone by) stays listed, marked, and no kit carries it.

import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ImagePlus, X } from 'lucide-react';
import { givmoClient, isGivmoError } from '@givmo/api-client';
import { Button, Card, CardBody, CardHeader, Icon, cn, testIdProp } from '@givmo/design-system';
import {
  CollateralPickerDialog,
  collateralOffered,
  fetchOrgCollateralItem,
  organizationDate,
  shareKitOptions,
  type CollateralItem,
} from '@givmo/org-console';
import { defaultZone } from './emails-decode';
import { useFundraisersModule } from './fundraisers-context';

export const SHARE_GRAPHICS_COPY = {
  label: 'Graphics to share',
  line: 'Everyone sharing this campaign or its fundraiser pages gets these to post with their link.',
  none: 'None yet. People who share get their link and the ready-made words.',
  choose: 'Choose graphics',
  change: 'Change graphics',
  pickerTitle: 'Graphics to share',
  pickerBody: (max: number): string =>
    `Choose up to ${max} from Collateral, in the order people should see them.`,
  use: 'Use these',
  remove: (title: string): string => `Remove ${title}`,
  earlier: (title: string): string => `Move ${title} earlier`,
  later: (title: string): string => `Move ${title} later`,
  notShared: 'Out of date, not shared',
  missing: 'No longer in Collateral',
  saveFailed: 'The graphics weren’t saved. Please try again.',
  /** The campaign's own Share card with none picked (it shows only the picks). */
  hint: 'No graphics yet.',
  hintLink: 'Pick them in Graphics to share.',
  /** The same line for a reader who can't pick them. */
  hintReadOnly: 'Graphics to share are picked by an Owner.',
} as const;
const C = SHARE_GRAPHICS_COPY;

/** The card's element id, for the Share card's pointer to it. */
export const SHARE_GRAPHICS_CARD_ID = 'p2p-share-graphics-card';

/**
 * The campaign's own Share card's line where it carries no graphics: none picked yet, and where (`onPick`,
 * for a person who can pick them), or who does.
 */
export function ShareGraphicsHint({ onPick }: { onPick?: () => void }) {
  return (
    <p
      className="text-s13 leading-h15 text-text-secondary"
      {...testIdProp('p2p-share-graphics-hint')}
    >
      {C.hint}{' '}
      {onPick === undefined ? (
        C.hintReadOnly
      ) : (
        <button
          type="button"
          onClick={onPick}
          className="font-w600 text-brand-600 hover:underline"
          {...testIdProp('p2p-share-graphics-hint-link')}
        >
          {C.hintLink}
        </button>
      )}
    </p>
  );
}

export interface ShareGraphicsPicks {
  itemIds: string[];
  limit: number;
}

/** The server's answer, read tolerantly: ids in order and the limit, or null for any other shape. */
export function decodeShareGraphics(body: unknown): ShareGraphicsPicks | null {
  if (typeof body !== 'object' || body === null) return null;
  const record = body as Record<string, unknown>;
  if (!Array.isArray(record.item_ids)) return null;
  const itemIds = record.item_ids.filter((id): id is string => typeof id === 'string');
  const limit = typeof record.limit === 'number' && record.limit > 0 ? record.limit : 8;
  return { itemIds, limit };
}

const path = (orgRef: string, campaignId: string) =>
  `/org/${encodeURIComponent(orgRef)}/fundraisers/${encodeURIComponent(campaignId)}/share-graphics`;

export const shareGraphicsKeys = {
  picks: (orgRef: string, campaignId: string) =>
    ['fundraisers-module', 'share-graphics', orgRef, campaignId] as const,
  item: (orgRef: string, itemId: string) =>
    ['fundraisers-module', 'share-graphics', orgRef, 'item', itemId] as const,
};

async function readPicks(orgRef: string, campaignId: string, signal?: AbortSignal) {
  const { data } = await givmoClient.get({
    url: path(orgRef, campaignId),
    signal,
    throwOnError: true,
  });
  return decodeShareGraphics(data);
}

async function savePicks(orgRef: string, campaignId: string, itemIds: readonly string[]) {
  const { data } = await givmoClient.put({
    url: path(orgRef, campaignId),
    body: { item_ids: itemIds },
    throwOnError: true,
  });
  return decodeShareGraphics(data);
}

/** Only Collateral's own 404 says a pick's item is gone; a read that failed says nothing about it. */
const isItemGone = (error: unknown): boolean => isGivmoError(error) && error.status === 404;

/** A route miss or a refusal is an answer (dark here, or not this person's): no retry. */
const retry = (failureCount: number, error: unknown) =>
  !(isGivmoError(error) && (error.status === 404 || error.status === 403)) && failureCount < 1;

/**
 * The campaign's picks still offered (current images in Collateral), in its order, for a host that starts
 * from them: a new Board Ask's attachments (D-EMC-23 (2) as amended). `settled` once every read has
 * answered; dark, a refusal or a failure reads as none, so a host never waits on it for long.
 */
export function useCampaignShareGraphicItems(
  orgRef: string,
  campaignId: string,
  today: string,
  enabled: boolean,
): { settled: boolean; items: CollateralItem[] } {
  const picks = useQuery({
    queryKey: shareGraphicsKeys.picks(orgRef, campaignId),
    enabled: enabled && orgRef.length > 0,
    retry,
    queryFn: ({ signal }) => readPicks(orgRef, campaignId, signal),
  });
  const ids = picks.data?.itemIds ?? [];
  const reads = useQueries({
    queries: ids.map((itemId) => ({
      queryKey: shareGraphicsKeys.item(orgRef, itemId),
      enabled,
      retry,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchOrgCollateralItem(orgRef, itemId, signal),
    })),
  });
  if (!enabled) return { settled: true, items: [] };
  const settled = !picks.isPending && reads.every((read) => !read.isPending);
  const items = reads.flatMap((read) =>
    read.data && collateralOffered(read.data, ['image'], today) ? [read.data] : [],
  );
  return { settled: settled || picks.isError, items: picks.isError ? [] : items };
}

export function CampaignShareGraphics({
  campaignId,
  canManage,
}: {
  campaignId: string;
  /** The campaign's settings rank (the server's own): choose, remove and reorder. */
  canManage: boolean;
}) {
  const config = useFundraisersModule();
  const orgRef = config.orgRef ?? '';
  const queryClient = useQueryClient();
  const [picking, setPicking] = useState(false);
  const picksKey = shareGraphicsKeys.picks(orgRef, campaignId);
  const picks = useQuery({
    queryKey: picksKey,
    enabled: orgRef.length > 0,
    retry,
    queryFn: ({ signal }) => readPicks(orgRef, campaignId, signal),
  });
  const ids = picks.data?.itemIds ?? [];
  const items = useQueries({
    queries: ids.map((itemId) => ({
      queryKey: shareGraphicsKeys.item(orgRef, itemId),
      retry,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchOrgCollateralItem(orgRef, itemId, signal),
    })),
  });
  const save = useMutation({
    mutationFn: (itemIds: readonly string[]) => savePicks(orgRef, campaignId, itemIds),
    onSuccess: (saved) => {
      if (saved !== null) queryClient.setQueryData(picksKey, saved);
      setPicking(false);
      // The campaign's own Share card shows the picks: it reads them again now, not at the next load.
      void queryClient.invalidateQueries({
        queryKey: shareKitOptions({ kind: 'organization', orgRef, fundraiserId: campaignId })
          .queryKey,
        exact: true,
      });
    },
    // A refused save names a pick that went away (deleted from Collateral since): read them again, so
    // trying again saves what's there rather than the same refused list.
    onError: () => void queryClient.invalidateQueries({ queryKey: picksKey }),
  });
  if (picks.data == null) return null;
  const limit = picks.data.limit;
  const today = organizationDate(defaultZone(undefined, config.orgTimeZone));
  const known = items.flatMap((read) => (read.data ? [read.data] : []));
  const editable = canManage && !save.isPending;
  const move = (from: number, to: number) => {
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved as string);
    save.mutate(next);
  };

  const chooseButton = canManage ? (
    <Button
      appearance="saas"
      tone="secondary"
      size="sm"
      className="self-start"
      leadingIcon={<Icon icon={ImagePlus} size={14} aria-hidden />}
      disabled={!editable}
      onClick={() => setPicking(true)}
      testId="p2p-share-graphics-choose"
    >
      {ids.length > 0 ? C.change : C.choose}
    </Button>
  ) : null;

  return (
    <Card flush id={SHARE_GRAPHICS_CARD_ID} className="scroll-mt-24" testId="p2p-share-graphics">
      <CardHeader title={C.label} subtitle={C.line} />
      <CardBody className="flex flex-col gap-14">
        {ids.length === 0 ? (
          <p className="text-s14 text-text-secondary" {...testIdProp('p2p-share-graphics-none')}>
            {C.none}
          </p>
        ) : (
          // Tiles from `sm` up; on a phone, one row each, its acts full-size beside it.
          <ol
            className="flex flex-col gap-10 sm:flex-row sm:flex-wrap sm:gap-12"
            {...testIdProp('p2p-share-graphics-list')}
          >
            {ids.map((itemId, index) => (
              <PickTile
                key={itemId}
                // A place only where there is an order to read: one picture is just the picture.
                place={ids.length > 1 ? index + 1 : null}
                item={items[index]?.data ?? null}
                gone={isItemGone(items[index]?.error)}
                shared={(() => {
                  const item = items[index]?.data;
                  return item ? collateralOffered(item, ['image'], today) : true;
                })()}
                editable={editable}
                first={index === 0}
                last={index === ids.length - 1}
                onRemove={() => save.mutate(ids.filter((id) => id !== itemId))}
                onEarlier={() => move(index, index - 1)}
                onLater={() => move(index, index + 1)}
                testId={`p2p-share-graphics-item-${itemId}`}
              />
            ))}
          </ol>
        )}
        {chooseButton}
        {save.isError && (
          <p
            role="alert"
            className="text-s13 text-danger-700"
            {...testIdProp('p2p-share-graphics-error')}
          >
            {C.saveFailed}
          </p>
        )}
      </CardBody>
      {canManage && (
        <CollateralPickerDialog
          open={picking}
          onOpenChange={setPicking}
          orgRef={orgRef}
          kinds={['image']}
          multiple
          ordered
          square
          max={limit}
          selectedIds={ids}
          knownItems={known}
          onChoose={(chosen: CollateralItem[]) => save.mutate(chosen.map((item) => item.id))}
          title={C.pickerTitle}
          body={C.pickerBody(limit)}
          confirmLabel={C.use}
          canUpload={config.permissions?.includes('profile:write') === true}
          today={today}
          applying={save.isPending}
          testId="p2p-share-graphics-picker"
        />
      )}
    </Card>
  );
}

function PickTile({
  place,
  item,
  gone,
  shared,
  editable,
  first,
  last,
  onRemove,
  onEarlier,
  onLater,
  testId,
}: {
  place: number | null;
  item: CollateralItem | null;
  /** Collateral answers it's not there: removed since (the server drops it from the picks too). */
  gone: boolean;
  /** Still current in Collateral, so the kits carry it. */
  shared: boolean;
  editable: boolean;
  first: boolean;
  last: boolean;
  onRemove: () => void;
  onEarlier: () => void;
  onLater: () => void;
  testId: string;
}) {
  const title = item?.title ?? '';
  const ordered = !first || !last;
  return (
    <li
      className="group flex min-w-0 items-center gap-12 sm:w-[136px] sm:flex-col sm:items-stretch sm:gap-6"
      {...testIdProp(testId)}
    >
      <div className="relative size-[64px] shrink-0 sm:size-auto sm:w-full">
        {item ? (
          <img
            src={item.url}
            alt=""
            className={cn(
              'aspect-square w-full rounded-lg border border-border-subtle bg-surface-sunken object-cover',
              !shared && 'opacity-40',
            )}
          />
        ) : (
          <div className="aspect-square w-full rounded-lg border border-dashed border-border-subtle bg-surface-sunken" />
        )}
        {place !== null && (
          <span
            aria-hidden
            className="absolute left-4 top-4 flex size-20 items-center justify-center rounded-full bg-surface text-s12 font-w700 text-text-strong shadow-sm ring-1 ring-border-subtle sm:left-8 sm:top-8 sm:size-22"
          >
            {place}
          </span>
        )}
        {editable && (
          <button
            type="button"
            aria-label={C.remove(title)}
            onClick={onRemove}
            className="absolute right-8 top-8 hidden size-24 items-center justify-center rounded-full bg-surface text-text-secondary shadow-sm ring-1 ring-border-subtle hover:text-text-strong focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-brand-500 sm:flex"
            {...testIdProp(`${testId}-remove`)}
          >
            <Icon icon={X} size={13} aria-hidden />
          </button>
        )}
        {editable && ordered && (
          <div className="absolute inset-x-8 bottom-8 hidden justify-between opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 sm:flex [@media(hover:none)]:opacity-100">
            <MoveButton
              label={C.earlier(title)}
              icon={ChevronLeft}
              hidden={first}
              onClick={onEarlier}
              testId={`${testId}-earlier`}
            />
            <MoveButton
              label={C.later(title)}
              icon={ChevronRight}
              hidden={last}
              onClick={onLater}
              testId={`${testId}-later`}
            />
          </div>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-none">
        <span
          className="line-clamp-2 text-s13 leading-h15 font-w500 text-text-strong"
          title={title}
        >
          {title}
        </span>
        {(gone || !shared) && (
          <span className="text-s12 text-warning-700">{gone ? C.missing : C.notShared}</span>
        )}
      </div>
      {editable && (
        <div className="flex shrink-0 items-center sm:hidden">
          {ordered && (
            <>
              <RowButton
                label={C.earlier(title)}
                icon={ChevronUp}
                disabled={first}
                onClick={onEarlier}
                testId={`${testId}-row-earlier`}
              />
              <RowButton
                label={C.later(title)}
                icon={ChevronDown}
                disabled={last}
                onClick={onLater}
                testId={`${testId}-row-later`}
              />
            </>
          )}
          <RowButton
            label={C.remove(title)}
            icon={X}
            onClick={onRemove}
            testId={`${testId}-row-remove`}
          />
        </div>
      )}
    </li>
  );
}

/** A phone row's act: the console's full touch target, quiet until pressed. */
function RowButton({
  label,
  icon,
  disabled = false,
  onClick,
  testId,
}: {
  label: string;
  icon: typeof X;
  disabled?: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-32 items-center justify-center rounded-md text-text-secondary hover:bg-surface-sunken hover:text-text-strong focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-brand-500 disabled:opacity-30"
      {...testIdProp(testId)}
    >
      <Icon icon={icon} size={16} aria-hidden />
    </button>
  );
}

function MoveButton({
  label,
  icon,
  hidden,
  onClick,
  testId,
}: {
  label: string;
  icon: typeof ChevronLeft;
  hidden: boolean;
  onClick: () => void;
  testId: string;
}) {
  if (hidden) return <span aria-hidden className="size-24" />;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex size-24 items-center justify-center rounded-full bg-surface text-text-secondary shadow-sm ring-1 ring-border-subtle hover:text-text-strong focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-brand-500"
      {...testIdProp(testId)}
    >
      <Icon icon={icon} size={14} aria-hidden />
    </button>
  );
}
